import {
  assertInstance,
  isEmpty,
  isOdd,
  notNullish,
  parseBoolean,
  parseJsonArray,
  parseNumber,
  parseOptionalNumber,
  parseRecord,
  parseString,
  /* tree-shaking no-side-effects-when-called */ parsingJsonObjectAs,
  /* tree-shaking no-side-effects-when-called */ parsingRecordAs,
  type TCoercer,
} from "@de-xima/xima-common-js-lang";
import $ from "jquery";
import Handsontable, { type GridSettings } from "handsontable";

export type TSheetType = "template" | "client";

interface IInternalData {
  allDay: boolean;
  dateFrom: string;
  dateUntil: string;
  id: number;
  lockingVersion: number | undefined;
  name: string;
  timeFrom: string;
  timeUntil: string;
}

interface IInternalDataTemplate extends IInternalData {}

interface IInternalDataClient extends IInternalData {
  excluded: boolean;
}

export interface TClosingTimeTemplate {
  allDay: boolean;
  dateFrom: string;
  dateUntil: string;
  id: number;
  lockingVersion: number | undefined;
  name: string;
  timeFrom: string;
  timeUntil: string;
}

export interface TClosingTimeClient extends TClosingTimeTemplate {
  excluded: boolean;
}

export interface TSheetConfig {
  dateFormat: string;
  i18n: SheetConfigI18n;
  lang: string;
  timeFormat: string;
}

export interface SheetConfigI18n {
  addButton: string;
  colActions: string;
  colAllDay: string;
  colDateFrom: string;
  colDateUntil: string;
  colExcluded: string;
  colName: string;
  colTimeFrom: string;
  colTimeUntil: string;
  defaultName: string;
  deleteButtonTitle: string;
  emptyMessage: string;
}

export interface ISheetHandler {
  convertExternalToInternalData: (config: TSheetConfig, data: unknown[]) => unknown[];
  convertInternalToExternalData: (config: TSheetConfig, data: unknown[]) => unknown[];
  createColumns: (config: TSheetConfig) => Handsontable.DefaultSettings["columns"];
  createColHeaders: (config: TSheetConfig) => string[];
  createDefaultData: (config: TSheetConfig) => object;
  columnIndex: Record<"allDay" | "dateFrom" | "dateUntil" | "actions" | "timeFrom" | "timeUntil" | "name", number>;
  colCount: number;
}

export interface ISheetWidget {
  config: TSheetConfig;
  container: HTMLElement;
  handler: ISheetHandler;
  input: HTMLInputElement;
  ht: Handsontable;
  type: TSheetType;
}

const parseClosingTimeTemplate: TCoercer<TClosingTimeTemplate> = parsingRecordAs({
  allDay: parseBoolean,
  dateFrom: parseString,
  dateUntil: parseString,
  id: parseNumber,
  lockingVersion: parseOptionalNumber,
  name: parseString,
  timeFrom: parseString,
  timeUntil: parseString,
});

const parseClosingTimeClient: TCoercer<TClosingTimeClient> = parsingRecordAs({
  allDay: parseBoolean,
  dateFrom: parseString,
  dateUntil: parseString,
  excluded: parseBoolean,
  id: parseNumber,
  lockingVersion: parseOptionalNumber,
  name: parseString,
  timeFrom: parseString,
  timeUntil: parseString,
});

const parseSheetConfig: TCoercer<TSheetConfig> = parsingJsonObjectAs({
  dateFormat: parseString,
  i18n: parsingRecordAs({
    addButton: parseString,
    colActions: parseString,
    colAllDay: parseString,
    colDateFrom: parseString,
    colDateUntil: parseString,
    colExcluded: parseString,
    colName: parseString,
    colTimeFrom: parseString,
    colTimeUntil: parseString,
    defaultName: parseString,
    deleteButtonTitle: parseString,
    emptyMessage: parseString,
  }),
  lang: parseString,
  timeFormat: parseString,
});

const ColumnName = {
  allDay: "allDay",
  dateFrom: "dateFrom",
  dateUntil: "dateUntil",
  name: "name",
  timeFrom: "timeFrom",
  timeUntil: "timeUntil",
};

const HandlerClient: ISheetHandler = {
  colCount: 8,
  columnIndex: {
    actions: 7,
    allDay: 6,
    dateFrom: 1,
    dateUntil: 2,
    name: 3,
    timeFrom: 4,
    timeUntil: 5,
  },
  convertExternalToInternalData: (config, items): IInternalDataClient[] => {
    return items
      .map((item) => parseClosingTimeClient(item))
      .map((item) => ({
        allDay: item.allDay,
        dateFrom: convertExternalToInternalDate(config.dateFormat, item.dateFrom),
        dateUntil: convertExternalToInternalDate(config.dateFormat, item.dateUntil),
        excluded: item.excluded,
        id: item.id,
        lockingVersion: item.lockingVersion,
        name: item.name,
        timeFrom: convertExternalToInternalTime(config.timeFormat, item.timeFrom),
        timeUntil: convertExternalToInternalTime(config.timeFormat, item.timeUntil),
      }));
  },
  convertInternalToExternalData: (config, items): TClosingTimeClient[] => {
    return items
      .map((item) => item as IInternalDataClient)
      .map((item) => ({
        allDay: item.allDay,
        dateFrom: convertInternalToExternalDate(config.dateFormat, item.dateFrom) ?? "",
        dateUntil: convertInternalToExternalDate(config.dateFormat, item.dateUntil) ?? "",
        excluded: item.excluded,
        id: item.id,
        lockingVersion: item.lockingVersion,
        name: item.name,
        timeFrom: convertInternalToExternalTime(config.timeFormat, item.timeFrom) ?? "",
        timeUntil: convertInternalToExternalTime(config.timeFormat, item.timeUntil) ?? "",
      }));
  },
  createColHeaders: ({ i18n }) => [
    i18n.colExcluded,
    i18n.colDateFrom,
    i18n.colDateUntil,
    i18n.colName,
    i18n.colTimeFrom,
    i18n.colTimeUntil,
    i18n.colAllDay,
    i18n.colActions,
  ],
  createColumns: (config) => [
    createColumnExcluded(config),
    createColumnDateFrom(config),
    createColumnDateUntil(config),
    createColumnName(config),
    createColumnTimeFrom(config),
    createColumnTimeUntil(config),
    createColumnAllDay(config),
    createColumnActions(config),
  ],
  createDefaultData: (config): TClosingTimeClient => {
    const date = getDefaultDate(config);
    const timeFrom = getDefaultTimeFrom(config);
    const timeUntil = getDefaultTimeUntil(config);
    return {
      allDay: true,
      dateFrom: date,
      dateUntil: date,
      excluded: false,
      id: -1,
      lockingVersion: undefined,
      name: config.i18n.defaultName,
      timeFrom: timeFrom,
      timeUntil: timeUntil,
    };
  },
};

const HandlerTemplate: ISheetHandler = {
  colCount: 7,
  columnIndex: {
    actions: 6,
    allDay: 5,
    dateFrom: 0,
    dateUntil: 1,
    name: 2,
    timeFrom: 3,
    timeUntil: 4,
  },
  convertExternalToInternalData: (config, data): IInternalDataTemplate[] => {
    return data
      .map((d) => parseClosingTimeTemplate(d))
      .map((item) => ({
        allDay: item.allDay,
        dateFrom: convertExternalToInternalDate(config.dateFormat, item.dateFrom),
        dateUntil: convertExternalToInternalDate(config.dateFormat, item.dateUntil),
        id: item.id,
        lockingVersion: item.lockingVersion,
        name: item.name,
        timeFrom: convertExternalToInternalTime(config.timeFormat, item.timeFrom),
        timeUntil: convertExternalToInternalTime(config.timeFormat, item.timeUntil),
      }));
  },
  convertInternalToExternalData: (config, items): TClosingTimeTemplate[] => {
    return items
      .map((item) => item as IInternalDataTemplate)
      .map((item) => ({
        allDay: item.allDay,
        dateFrom: convertInternalToExternalDate(config.dateFormat, item.dateFrom) ?? "",
        dateUntil: convertInternalToExternalDate(config.dateFormat, item.dateUntil) ?? "",
        id: item.id,
        lockingVersion: item.lockingVersion,
        name: item.name,
        timeFrom: convertInternalToExternalTime(config.timeFormat, item.timeFrom) ?? "",
        timeUntil: convertInternalToExternalTime(config.timeFormat, item.timeUntil) ?? "",
      }));
  },
  createColHeaders: ({ i18n }) => [
    i18n.colDateFrom,
    i18n.colDateUntil,
    i18n.colName,
    i18n.colTimeFrom,
    i18n.colTimeUntil,
    i18n.colAllDay,
    i18n.colActions,
  ],
  createColumns: (config) => [
    createColumnDateFrom(config),
    createColumnDateUntil(config),
    createColumnName(config),
    createColumnTimeFrom(config),
    createColumnTimeUntil(config),
    createColumnAllDay(config),
    createColumnActions(config),
  ],
  createDefaultData: (config): TClosingTimeTemplate => {
    const date = getDefaultDate(config);
    const timeFrom = getDefaultTimeFrom(config);
    const timeUntil = getDefaultTimeUntil(config);
    return {
      allDay: true,
      dateFrom: date,
      dateUntil: date,
      id: -1,
      lockingVersion: undefined,
      name: config.i18n.defaultName,
      timeFrom: timeFrom,
      timeUntil: timeUntil,
    };
  },
};

const Handlers = {
  client: HandlerClient,
  template: HandlerTemplate,
};

function getMoment(): typeof import("moment") {
  // moment global is shipped with PrimeFaces sheet script
  return parseRecord(window).moment as typeof import("moment");
}

function parseMoment(dateFormat: string, date: string | undefined): moment.Moment | undefined {
  if (isEmpty(date)) {
    return undefined;
  }
  const parsed = getMoment()(date, dateFormat);
  return parsed.isValid() ? parsed : undefined;
}

function removeChange(changes: [number, string | number, unknown, unknown][], row: number, col: string | number): void {
  for (let i = changes.length - 1; i >= 0; i -= 1) {
    const change = changes[i];
    if (change !== undefined && change[0] === row && change[1] === col) {
      changes.splice(i, 1);
    }
  }
}

function getDefaultDate(config: TSheetConfig): string {
  const currentYear = new Date().getFullYear();
  return getMoment()(new Date(currentYear, 0, 1, 0, 0, 0)).format(config.dateFormat);
}

function getDefaultTimeFrom(config: TSheetConfig): string {
  return getMoment()(new Date(1970, 0, 1, 0, 0, 0)).format(config.timeFormat);
}

function getDefaultTimeUntil(config: TSheetConfig): string {
  return getMoment()(new Date(1970, 0, 1, 23, 59, 0)).format(config.timeFormat);
}

function createColumnActions(config: TSheetConfig): Record<string, unknown> {
  return {
    copyable: false,
    readOnly: true,
    renderer: (
      instance: Handsontable,
      td: HTMLElement,
      row: number,
      _col: number,
      _prop: string | number,
      _value: unknown,
      _cellProperties: GridSettings,
    ) => {
      td.innerHTML = "";

      const buttonList = document.createElement("div");
      buttonList.classList.add("closing-times-sheet__actions");

      const icon = document.createElement("span");
      icon.classList.add("ui-button-icon-left", "ui-icon", "ui-c", "ui-icon-delete");
      const deleteButton = document.createElement("button");
      deleteButton.type = "button";
      deleteButton.addEventListener("click", (event) => {
        instance.alter("remove_row", row);
        event.stopPropagation();
      });
      deleteButton.classList.add("closing-times-sheet__button", "closing-times-sheet__delete-button");
      deleteButton.title = config.i18n.deleteButtonTitle;
      deleteButton.appendChild(icon);

      buttonList.appendChild(deleteButton);
      td.appendChild(buttonList);
      return td;
    },
    trimWhitespace: true,
    width: 50,
    wordWrap: true,
  };
}

function createColumnDateFrom(config: TSheetConfig): Record<string, unknown> {
  return {
    copyable: true,
    correctFormat: true,
    data: "dateFrom",
    dateFormat: config.dateFormat,
    datePickerConfig: { minDate: undefined },
    trimWhitespace: true,
    type: "date",
    width: 100,
    wordWrap: true,
  };
}

function createColumnDateUntil(config: TSheetConfig): Record<string, unknown> {
  return {
    copyable: true,
    correctFormat: true,
    data: "dateUntil",
    dateFormat: config.dateFormat,
    trimWhitespace: true,
    type: "date",
    width: 100,
    wordWrap: true,
  };
}
function createColumnName(_config: TSheetConfig): Record<string, unknown> {
  return {
    copyable: true,
    data: "name",
    trimWhitespace: true,
    type: "text",
    width: 100,
    wordWrap: true,
  };
}
function createColumnTimeFrom(config: TSheetConfig): Record<string, unknown> {
  return {
    copyable: true,
    correctFormat: true,
    data: "timeFrom",
    placeholder: getDefaultTimeFrom(config),
    timeFormat: config.timeFormat,
    trimWhitespace: true,
    type: "time",
    width: 100,
    wordWrap: true,
  };
}

function createColumnTimeUntil(config: TSheetConfig): Record<string, unknown> {
  return {
    copyable: true,
    correctFormat: true,
    data: "timeUntil",
    placeholder: getDefaultTimeUntil(config),
    timeFormat: config.timeFormat,
    trimWhitespace: true,
    type: "time",
    width: 100,
    wordWrap: true,
  };
}

function createColumnAllDay(_config: TSheetConfig): Record<string, unknown> {
  return {
    copyable: true,
    data: "allDay",
    trimWhitespace: true,
    type: "checkbox",
    width: 100,
    wordWrap: true,
  };
}

function createColumnExcluded(_config: TSheetConfig): Record<string, unknown> {
  return {
    copyable: true,
    data: "excluded",
    trimWhitespace: true,
    type: "checkbox",
    width: 100,
    wordWrap: true,
  };
}

function convertExternalToInternalDate(dateFormat: string, date: string): string {
  if (isEmpty(date)) {
    return "";
  }
  const parts = date.split("-");
  const year = Number.parseInt(parts[0] ?? "", 10);
  const month = Number.parseInt(parts[1] ?? "", 10);
  const day = Number.parseInt(parts[2] ?? "", 10);
  if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) {
    return "";
  }
  return getMoment()(new Date(year, month - 1, day)).format(dateFormat);
}

function convertExternalToInternalTime(timeFormat: string, time: string): string {
  if (isEmpty(time)) {
    return "";
  }
  const parts = time.split("-");
  const hour = Number.parseInt(parts[0] ?? "", 10);
  const minute = Number.parseInt(parts[1] ?? "", 10);
  if (Number.isNaN(hour) || Number.isNaN(minute)) {
    return "";
  }
  return getMoment()(new Date(1970, 0, 1, hour, minute)).format(timeFormat);
}

function convertInternalToExternalDate(dateFormat: string, date: string): string | undefined {
  if (isEmpty(date)) {
    return undefined;
  }
  const parsed = parseMoment(dateFormat, date);
  if (parsed === undefined) {
    return undefined;
  }
  const year = parsed.year();
  const month = parsed.month();
  const day = parsed.date();
  if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) {
    return undefined;
  }
  return `${year}-${month + 1}-${day}`;
}

function convertInternalToExternalTime(timeFormat: string, time: string): string | undefined {
  if (isEmpty(time)) {
    return undefined;
  }
  const parsed = parseMoment(timeFormat, time);
  if (parsed === undefined) {
    return undefined;
  }
  const hour = parsed.hour();
  const minute = parsed.minute();
  if (Number.isNaN(hour) || Number.isNaN(minute)) {
    return undefined;
  }
  return `${hour}-${minute}`;
}

/**
 * For a given row, update the read-only state of the time inputs. When the all day
 * checkbox is checked, the time inputs are disabled. Otherwise, they are enabled.
 * @param table Sheet to update.
 * @param row  0-based index of the row to update.
 * @param allDay Whether the all day checkbox of the given row is checked.
 */
function updateReadOnlyStateAllDay(widget: ISheetWidget, row: number, allDay: boolean) {
  if (allDay) {
    widget.ht.setCellMeta(
      row,
      widget.handler.columnIndex.timeFrom,
      "editor",
      // @ts-expect-error Handsontable type declarations are wrong
      false,
    );
    widget.ht.setCellMeta(
      row,
      widget.handler.columnIndex.timeUntil,
      "editor",
      // @ts-expect-error Handsontable type declarations are wrong
      false,
    );
    widget.ht.setCellMeta(row, widget.handler.columnIndex.timeFrom, "className", "ui-datatable-row-disabled");
    widget.ht.setCellMeta(row, widget.handler.columnIndex.timeUntil, "className", "ui-datatable-row-disabled");
  } else {
    widget.ht.removeCellMeta(row, widget.handler.columnIndex.timeFrom, "editor");
    widget.ht.removeCellMeta(row, widget.handler.columnIndex.timeUntil, "editor");
    widget.ht.removeCellMeta(row, widget.handler.columnIndex.timeFrom, "className");
    widget.ht.removeCellMeta(row, widget.handler.columnIndex.timeUntil, "className");
  }
}

function updateMinDate(widget: ISheetWidget, row: number, dateFrom: unknown) {
  const from = parseMoment(widget.config.dateFormat, parseString(dateFrom));
  if (from !== undefined) {
    widget.ht.setCellMeta(
      row,
      widget.handler.columnIndex.dateUntil,
      "datePickerConfig",
      // @ts-expect-error Handsontable type declarations are wrong
      { minDate: from.toDate() },
    );
  } else {
    widget.ht.removeCellMeta(row, widget.handler.columnIndex.dateUntil, "datePickerConfig");
  }
}

function updateEmptyData(widget: ISheetWidget, data: unknown[]) {
  if (data.length === 0) {
    const colSpan = widget.handler.colCount + 1;
    const info = document.createTextNode(widget.config.i18n.emptyMessage);

    const buttonIcon = document.createElement("span");
    buttonIcon.classList.add("ui-button-icon-left", "ui-icon", "ui-c", "ico-fc-plus-circle-outline");
    const buttonText = document.createElement("span");
    buttonText.classList.add("ui-button-text", "ui-c");
    buttonText.textContent = widget.config.i18n.addButton;
    const button = document.createElement("button");
    button.classList.add(
      "ui-button",
      "ui-widget",
      "ui-state-default",
      "ui-corner-all",
      "ui-button-icon-only",
      "button-borderless",
      "add-template-closing-time",
    );
    button.type = "button";
    button.title = widget.config.i18n.addButton;
    button.appendChild(buttonIcon);
    button.appendChild(buttonText);
    button.addEventListener("click", () => addRow(widget));

    const td = document.createElement("td");
    td.colSpan = colSpan;

    const tr = document.createElement("tr");

    tr.appendChild(td);
    td.appendChild(info);
    td.appendChild(button);

    const tbody = widget.container.querySelector(".emptyRows tbody");
    if (notNullish(tbody)) {
      tbody.classList.add("empty-message");
      tbody.appendChild(tr);
    }
  }
}

/**
 * Applies the initial state to the sheet. Disables all date inputs for rows
 * where the all day checkbox is checked.
 * @param sheet Sheet to update.
 */
function applyInitialState(widget: ISheetWidget): void {
  const rows = widget.ht.countRows();
  for (let row = 0; row < rows; row += 1) {
    const allDay = parseBoolean(widget.ht.getDataAtCell(row, widget.handler.columnIndex.allDay));
    updateReadOnlyStateAllDay(widget, row, allDay);
    updateMinDate(widget, row, widget.ht.getDataAtCell(row, widget.handler.columnIndex.dateFrom));
  }
}

function addRow(widget: ISheetWidget): void {
  $(widget.container).find(".emptyRows").find("tbody.empty-message").removeClass("empty-message").empty();
  widget.ht.alter("insert_row");
}

function createOnAfterRenderer(_widget: ISheetWidget): Handsontable.Hooks["afterRenderer"] {
  return (td, row) => {
    const styleClass: string[] = [];

    styleClass.push(td.className);

    // every other row highlighting
    if (isOdd(row)) {
      styleClass.push("ui-datatable-odd");
    } else {
      styleClass.push("ui-datatable-even");
    }

    td.className = styleClass.join(" ");
  };
}

function createOnAfterRemoveRow(widget: ISheetWidget): Handsontable.Hooks["afterRemoveRow"] {
  return () => {
    $(() => {
      updateEmptyData(widget, widget.ht.getSourceData());
    });
  };
}

function createOnAfterChange(widget: ISheetWidget): Handsontable.Hooks["afterChange"] {
  return (changes) => {
    if (!changes) {
      return;
    }
    for (const [row, col, , newValue] of changes) {
      if (col === ColumnName.allDay) {
        const allDay = parseBoolean(newValue);
        updateReadOnlyStateAllDay(widget, row, allDay);
      }
      if (col === ColumnName.dateFrom) {
        updateMinDate(widget, row, newValue);
      }
    }
    widget.ht.render();
  };
}

function createOnBeforeRenderer(widget: ISheetWidget): Handsontable.Hooks["afterCreateRow"] {
  return (index, amount) => {
    $(() => {
      for (let row = index; row < index + amount; row += 1) {
        const allDay = parseBoolean(widget.ht.getDataAtCell(row, widget.handler.columnIndex.allDay));
        updateReadOnlyStateAllDay(widget, row, allDay);
      }
      widget.ht.render();
    });
  };
}

function createOnBeforeChange(widget: ISheetWidget): Handsontable.Hooks["beforeChange"] {
  return (changes) => {
    if (!changes) {
      return;
    }

    // Create a fresh copy of the data for temporary modifications
    // Apply all changes to get a preview of the data with the changes applied
    const newData = (widget.ht.getSourceData() as Record<string, unknown>[]).map((x) => ({ ...x }));
    for (const [row, col, , newValue] of changes) {
      const item = newData[row];
      if (item !== undefined) {
        item[col] = newValue;
      }
    }

    const data = newData as unknown as IInternalDataTemplate[];

    // When the upper limit of a date or time range is before the lower limit,
    // increase the upper limit.
    const changedRows = new Set(changes.map((c) => c[0]));
    for (const row of changedRows) {
      const dateFrom = parseMoment(widget.config.dateFormat, data[row]?.dateFrom);
      const dateUntil = parseMoment(widget.config.dateFormat, data[row]?.dateUntil);
      const timeFrom = parseMoment(widget.config.timeFormat, data[row]?.timeFrom);
      const timeUntil = parseMoment(widget.config.timeFormat, data[row]?.timeUntil);
      const name = parseString(data[row]?.name);
      const allDay = parseBoolean(data[row]?.allDay);
      if (dateFrom !== undefined && dateUntil !== undefined) {
        if (dateFrom.isAfter(dateUntil, "days")) {
          const newDate = dateFrom.format(widget.config.dateFormat);
          removeChange(changes, row, ColumnName.dateUntil);
          changes.push([row, ColumnName.dateUntil, data[row]?.dateFrom, newDate]);
        }
      }
      if (dateFrom === undefined) {
        const newDate =
          dateUntil !== undefined ? dateUntil.format(widget.config.dateFormat) : getDefaultDate(widget.config);
        removeChange(changes, row, ColumnName.dateFrom);
        changes.push([row, ColumnName.dateFrom, data[row]?.dateFrom, newDate]);
      }
      if (dateUntil === undefined) {
        const newDate =
          dateFrom !== undefined ? dateFrom.format(widget.config.dateFormat) : getDefaultDate(widget.config);
        removeChange(changes, row, ColumnName.dateUntil);
        changes.push([row, ColumnName.dateUntil, data[row]?.dateUntil, newDate]);
      }
      if (timeFrom !== undefined && timeUntil !== undefined) {
        if (timeFrom.isAfter(timeUntil, "minutes")) {
          const newTime = timeFrom.format(widget.config.timeFormat);
          removeChange(changes, row, ColumnName.timeUntil);
          changes.push([row, ColumnName.timeUntil, data[row]?.timeUntil, newTime]);
        }
      }
      if (timeFrom === undefined && !allDay) {
        removeChange(changes, row, ColumnName.timeFrom);
        changes.push([row, ColumnName.timeFrom, data[row]?.timeFrom, getDefaultTimeFrom(widget.config)]);
      }
      if (timeUntil === undefined && !allDay) {
        removeChange(changes, row, ColumnName.timeUntil);
        changes.push([row, ColumnName.timeUntil, data[row]?.timeUntil, getDefaultTimeUntil(widget.config)]);
      }
      if (isEmpty(name)) {
        removeChange(changes, row, ColumnName.name);
        changes.push([row, ColumnName.name, data[row]?.name, widget.config.i18n.defaultName]);
      }
    }
  };
}

function createOnSave(widget: ISheetWidget): () => void {
  return () => {
    const json = widget.handler.convertInternalToExternalData(widget.config, widget.ht.getSourceData());
    widget.input.value = JSON.stringify(json);
  };
}

function createHandsonConfig(type: TSheetType, config: TSheetConfig, data: unknown[]): Handsontable.DefaultSettings {
  const handsonConfig: Handsontable.DefaultSettings = {
    allowInvalid: false,
    autoRowSize: false,
    colHeaders: Handlers[type].createColHeaders(config),
    contextMenu: ["row_above", "row_below", "remove_row"],
    data: data,
    dataSchema: Handlers[type].createDefaultData(config),
    enterMoves: { col: 1, row: 0 },
    height: 400,
    language: "en-US",
    manualColumnMove: false,
    manualColumnResize: true,
    manualRowMove: false,
    manualRowResize: false,
    minCols: 0,
    minRows: 0,
    readOnly: false,
    rowHeaders: true,
    selectionMode: "range",
    stretchH: "all",
  };
  const columns = Handlers[type].createColumns(config);
  if (columns !== undefined) {
    handsonConfig.columns = columns;
  }
  return handsonConfig;
}

function fixHandsontable(widget: ISheetWidget, data: unknown[]): void {
  $(widget.container).find(".htCore > tbody").addClass("ui-datatable-data ui-widget-content");
  updateEmptyData(widget, data);
}

export async function setupClosingTimesSheet(type: TSheetType, container: HTMLElement): Promise<ISheetWidget> {
  return new Promise((resolve, reject) => {
    $(() => {
      try {
        const handler = Handlers[type];

        const input = document.getElementById(`${container.id}_input`);
        assertInstance(input, HTMLInputElement);

        const configSpan = document.getElementById(`${container.id}_config`);
        assertInstance(configSpan, HTMLElement);

        const addButton = document.getElementById(`${container.id}_add`);

        const config = parseSheetConfig(configSpan.textContent ?? "");

        const json = parseJsonArray(input.value);
        const data = handler.convertExternalToInternalData(config, json);
        const handsonConfig = createHandsonConfig(type, config, data);

        const ht = new Handsontable(container, handsonConfig);

        const widget: ISheetWidget = { config, container, handler, ht, input, type };

        fixHandsontable(widget, data);

        ht.addHook("beforeChange", createOnBeforeChange(widget));
        ht.addHook("afterChange", createOnAfterChange(widget));
        ht.addHook("afterCreateRow", createOnBeforeRenderer(widget));
        ht.addHook("afterRenderer", createOnAfterRenderer(widget));
        ht.addHook("afterRemoveRow", createOnAfterRemoveRow(widget));

        $(document).on("fc-save", createOnSave(widget));

        addButton?.addEventListener("click", () => addRow(widget));

        applyInitialState(widget);

        $(() => ht.render());

        resolve(widget);
      } catch (e) {
        reject(e);
      }
    });
  });
}
